/**MTNSettings.js — Settings & Administration */
import React, { useState } from "react";
import { useMTN } from "@/context/MTNContext";
import { Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TABS=[{id:"methodology",label:"Methodology"},{id:"evidence",label:"Evidence Governance"},{id:"ef",label:"Emission Factors"},{id:"registry",label:"Registry"},{id:"users",label:"User & Access"}];

export default function MTNSettings() {
  const { loading } = useMTN();
  const [activeTab,setActiveTab]=useState("methodology");
  const [saving,setSaving]=useState(false);

  const handleSave=()=>{ setSaving(true); setTimeout(()=>{ setSaving(false); toast.success("Settings saved"); },1000); };

  if(loading)return<div style={{padding:40,color:"#64748b",fontSize:12}}>Loading…</div>;

  return(
    <div style={{padding:"24px 28px"}}>
      <div style={{marginBottom:20}}>
        <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
        <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>Settings & Administration</h1>
        <p style={{fontSize:11,color:"#475569"}}>Methodology governance · Emission factors · Evidence rules · Registry · Access control</p>
      </div>

      <div style={{display:"flex",gap:4,background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:6,marginBottom:16}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{flex:1,padding:"7px 8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:10,fontWeight:600,
              background:activeTab===t.id?"#FFCB00":"transparent",color:activeTab===t.id?"#000":"#475569"}}
            data-testid={`settings-tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      {activeTab==="methodology" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="settings-methodology">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>GHG Protocol Configuration</p>
            {[["Standard","GHG Protocol Corporate Standard (2015)"],["Scope 2 Method","Market-based (I-RECs applicable)"],["Scope 3 Category","Full value chain (Cat.1+4+11+12)"],["Reporting Boundary","Operational control"],["Base Year","FY 2025/2026"],["Restatement Policy","Material threshold: ±5%"]].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:8,borderBottom:"1px solid #0f172a",paddingBottom:6}}>
                <span style={{fontSize:9,color:"#64748b"}}>{k}</span>
                <span style={{fontSize:9,fontWeight:600,color:"#f1f5f9"}}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Carbon Market Methodology</p>
            {[["VCM Standard","Verra VCS (primary)"],["Alternative","Gold Standard"],["Art.6 Standard","PACM (Art.6.4)"],["Tower PoA Method","AMS-I.D / VM0038"],["Data Center","PAMS"],["Fleet","AMS-III.S"],["E-waste","Verra PCR"]].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:8,borderBottom:"1px solid #0f172a",paddingBottom:6}}>
                <span style={{fontSize:9,color:"#64748b"}}>{k}</span>
                <span style={{fontSize:9,fontWeight:600,color:"#60a5fa"}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab==="evidence" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="settings-evidence">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Evidence Governance Rules</p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[["Minimum evidence level for VVB","Declared (T2) — Measured preferred"],["Evidence expiry period","12 months from document date"],["T1 threshold","Automated meter data + calibration cert"],["T2 threshold","Invoice/declaration + chain of custody"],["T3 threshold","Engineering estimate + peer review"],["Required for Carbon Markets","T1/T2 minimum — estimated blocks VVB"],["Smart meter deployment target","Top 5,000 diesel-heavy towers by 2026"],["Evidence audit frequency","Quarterly internal + annual external"],].map(([k,v])=>(
              <div key={k} style={{background:"#060c1a",borderRadius:6,padding:10}}>
                <p style={{fontSize:8,color:"#475569",marginBottom:2}}>{k}</p>
                <p style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab==="ef" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="settings-ef">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Emission Factors — GHG Protocol Approved</p>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{borderBottom:"1px solid #1e293b"}}>
              {["Source","Factor","Unit","Source/Year"].map(h=><th key={h} style={{textAlign:"left",padding:"6px 10px",fontSize:8,color:"#475569",fontWeight:600}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {[["Diesel","2.68","kg CO₂e/L","IPCC 2006 Tier 1"],["Petrol","2.31","kg CO₂e/L","IPCC 2006 Tier 1"],["Natural Gas","2.02","kg CO₂e/m³","IPCC 2006"],["Nigeria Grid","0.43","kg CO₂e/kWh","IEA 2024 — Nigeria"],["Road Freight","0.21","kg CO₂e/km","GHG Protocol"],["Air Travel","0.285","kg CO₂e/km","ICAO 2024"],["Refrigerant (R-134a)","1,430","kg CO₂e/kg","IPCC AR5 GWP"],["Device Lifecycle","8.5","kg CO₂e/sub/yr","GSMA 2023 estimate"]].map(([s,f,u,src])=>(
              <tr key={s} style={{borderBottom:"1px solid #0f172a"}}>
                <td style={{padding:"6px 10px",fontSize:9,fontWeight:600,color:"#f1f5f9"}}>{s}</td>
                <td style={{padding:"6px 10px",fontSize:9,fontWeight:900,color:"#FFCB00"}}>{f}</td>
                <td style={{padding:"6px 10px",fontSize:9,color:"#94a3b8"}}>{u}</td>
                <td style={{padding:"6px 10px",fontSize:8,color:"#64748b"}}>{src}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {activeTab==="registry" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="settings-registry">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Registry & Verification Settings</p>
          {[["Primary Registry","Verra VCS","Active — PoA structure required for distributed towers"],["Secondary Registry","Gold Standard","Rural renewable pathway"],["Art.6 Registry","PACM","Early exploratory — NCCC not yet engaged"],["VVB Engagement","Pre-screening stage","DOE pre-screening pending baseline completion"],["NCCC Status","Not yet engaged","Critical path for Art.6 — initiate Q2 2026"],["LoA Status","Not initiated","Depends on NCCC engagement"],["Report QR Verification","Active","All PDF reports QR-registered in DeCarbonIQ"],].map(([k,v,n])=>(
            <div key={k} style={{marginBottom:10,borderBottom:"1px solid #0f172a",paddingBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <span style={{fontSize:9,color:"#94a3b8"}}>{k}</span>
                <span style={{fontSize:9,fontWeight:700,color:"#FFCB00"}}>{v}</span>
              </div>
              {n&&<p style={{fontSize:8,color:"#64748b",marginTop:2}}>{n}</p>}
            </div>
          ))}
        </div>
      )}

      {activeTab==="users" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="settings-users">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>User & Access Controls</p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[["Workspace Role","mtn"],["Subscription Tier","Enterprise"],["Workspace Isolation","Enforced — MTN only"],["Data Access","All 7 BUs + 8 projects"],["Report Access","All 6 PDF families"],["Carbon Market Access","Full — Art.6 conservatively disclosed"]].map(([k,v])=>(
              <div key={k} style={{background:"#060c1a",borderRadius:6,padding:10}}>
                <p style={{fontSize:8,color:"#475569",marginBottom:2}}>{k}</p>
                <p style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{v}</p>
              </div>
            ))}
          </div>
          <div style={{background:"#14532d30",border:"1px solid #22c55e30",borderRadius:6,padding:10}}>
            <div style={{display:"flex",gap:6}}>
              <CheckCircle2 size={12} color="#22c55e"/>
              <div>
                <p style={{fontSize:9,fontWeight:700,color:"#22c55e"}}>Workspace Isolation Active</p>
                <p style={{fontSize:9,color:"#94a3b8"}}>MTN workspace is fully isolated. No cross-tenant data access. Lagos, Nosak, NB, NIRSAL routes return HTTP 403 for MTN users.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{marginTop:16}}>
        <button onClick={handleSave} disabled={saving}
          style={{background:"#FFCB00",color:"#000",border:"none",borderRadius:6,padding:"8px 20px",fontSize:10,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}
          data-testid="settings-save-btn">
          {saving?<><Save size={11}/>Saving…</>:<><Save size={11}/>Save Settings</>}
        </button>
      </div>
    </div>
  );
}
